'use client';
import Image from 'next/image';
import { cn } from '@shadcn/lib/utils';
import { Course, CoursePicture } from '@/payload/payload-types';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@shadcn/components/ui/dialog';
import { useTrackEvent } from '../../_providers/TrackEventProvider';
import Play from '../../_icons/Play';
import { useTranslations } from 'next-intl';
import { WaitingListForm } from '../Forms/WaitingListForm';
import { Fragment, useState } from 'react';
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerTitle,
	DrawerTrigger,
} from '@shadcn/components/ui/drawer';
import { Button } from '../Button';
import Attention from '../../_icons/Attention';
import ChevronRight from '../../_icons/Chevron-right';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { ScrollArea } from '@shadcn/components/ui/scroll-area';

export const CourseCardWaitingList: React.FC<{
	course: Course;
	className?: string;
	type?: 'mobile' | 'desktop';
}> = ({ course, className, type = 'desktop' }) => {
	const trackEvent = useTrackEvent();
	const t = useTranslations('Course');

	const coursePicture = course.picture as CoursePicture;
	const courseImage = coursePicture.sizes?.card?.url || coursePicture.url;
	const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

	const CardContentShared = () => {
		return (
			<div className="rounded-3xl px-8 py-8">
				<div className="mb-6 flex flex-col">
					<p className="text-gold-light mb-4 text-xl font-bold">{course.title}</p>
					<p className="mb-4 text-sm text-white">
						{t('course-in-waitinglist-description')}
					</p>
					<WaitingListForm courseId={course.id} />
				</div>
			</div>
		);
	};

	if (type === 'mobile') {
		return (
			<Drawer
				repositionInputs={false}
				open={isDrawerOpen}
				onOpenChange={isOpen => {
					setIsDrawerOpen(isOpen);
				}}
			>
				<DrawerTrigger
					asChild
					className="bg-muted-navy fixed bottom-0 z-10 flex h-fit w-screen rounded-t-3xl p-4 lg:hidden"
				>
					<div className="flex flex-col items-start justify-start space-y-2">
						<Button
							variant="solid"
							intent="secondary"
							size="small"
							className="w-full flex-1"
						>
							{t('register-waitinglist')}
							<ChevronRight />
						</Button>
						<p className="text-muted-blue flex items-center text-sm italic">
							<span className="mr-2">
								<Attention color="#8EA0BB"></Attention>
							</span>
							{t('cannot-buy')}
						</p>
					</div>
				</DrawerTrigger>
				<VisuallyHidden.Root>
					<DrawerTitle>Waiting list</DrawerTitle>
				</VisuallyHidden.Root>
				<VisuallyHidden.Root>
					<DrawerDescription>
						Form for joining the waiting list for the course
					</DrawerDescription>
				</VisuallyHidden.Root>
				<DrawerContent className="bg-muted-navy h-[80vh] rounded-t-3xl">
					<DrawerClose className="absolute top-4 right-4" asChild>
						<Button intent="tertiary" variant="ghost" className="px-4 py-6">
							X
						</Button>
					</DrawerClose>
					<ScrollArea>
						<CardContentShared />
					</ScrollArea>
				</DrawerContent>
			</Drawer>
		);
	}
	return (
		<div className={cn('bg-muted-navy hidden rounded-3xl lg:block', className)}>
			<div className="relative">
				{courseImage && (
					<Image
						alt={coursePicture.alt}
						className="rounded-3xl"
						height={200}
						src={courseImage}
						width={400}
						style={{ objectFit: 'cover', height: '200px' }}
					/>
				)}
				{course.videoUrl && (
					<Dialog>
						<DialogTrigger
							onClick={() => {
								trackEvent('course_video_clicked', {
									course_name: course.title,
									component: 'desktop_side_card',
									course_discount_value_eur: null,
								});
							}}
							className="group absolute inset-0 flex cursor-pointer items-center justify-center rounded-3xl bg-[#041E413D]/25"
						>
							<div className="flex h-full flex-col items-center justify-center space-y-4">
								<Play />

								<p className="text-sm leading-6 font-medium text-white group-hover:underline">
									{t('explore-course-content')}
								</p>
							</div>
						</DialogTrigger>
						<DialogContent className="h-4/5 max-h-screen w-4/5 max-w-screen">
							<DialogTitle>Video</DialogTitle>
							<iframe
								width="100%"
								height="100%"
								src={`${course.videoUrl}&autoplay=1`}
								title="YouTube video player"
								allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
								referrerPolicy="strict-origin-when-cross-origin"
								allowFullScreen
								style={{ borderRadius: 24 }}
							></iframe>
						</DialogContent>
					</Dialog>
				)}
			</div>
			<CardContentShared />
		</div>
	);
};
